import { createAction, props } from '@ngrx/store';
import type { BiomapHabitatClassification } from '../../../interfaces/biomap/IBiomapLayer';
import type {
  IBiomapHabitatCompensationResponse,
  IBiomapMsaCompensationResponse,
} from '../../../interfaces/strategic-view/IBiomapCompensation';
import type {
  BiomapInterventionGeometry,
  BiomapInterventionGeometryTarget,
  IBiomapHabitatInterventionResponse,
  IBiomapInterventionAvailability,
  IBiomapInterventionProductSelection,
  IBiomapInterventionRunPlan,
  IBiomapMsaInterventionResponse,
} from '../../../interfaces/strategic-view/IBiomapInterventionAssessment';
import type {
  IBiomapMsaScenarioAnalysisRequest,
  IBiomapMsaScenarioAnalysisResponse,
  IBiomapScenarioCatalog,
  IBiomapScenarioSelection,
} from '../../../interfaces/strategic-view/IBiomapScenarioAnalysis';
import type {
  BiomapStrategicChangeProduct,
  IBiomapStrategicChangeResponse,
} from '../../../interfaces/strategic-view/IBiomapStrategicChange';
import {
  IStrategicComparisonResponse,
  IStrategicOverviewResponse,
  StrategicComparisonMode,
} from '../../../interfaces/strategic-view/IStrategicView';

export const loadStrategicRasterOverview = createAction(
  '[Strategic View] Load Raster Overview',
  props<{ projectUuid: string; rasterLayerUuid: string }>(),
);

export const loadStrategicRasterOverviewSuccess = createAction(
  '[Strategic View] Load Raster Overview Success',
  props<{ rasterLayerUuid: string; overview: IStrategicOverviewResponse }>(),
);

export const loadStrategicRasterOverviewFailure = createAction(
  '[Strategic View] Load Raster Overview Failure',
  props<{ rasterLayerUuid: string; error: any }>(),
);

export const loadStrategicRasterComparison = createAction(
  '[Strategic View] Load Raster Comparison',
  props<{
    projectUuid: string;
    rasterLayerUuid: string;
    sourceAUuid: string;
    sourceBUuid: string;
    comparisonMode?: StrategicComparisonMode;
    forceRecompute?: boolean;
  }>(),
);

export const loadStrategicRasterComparisonSuccess = createAction(
  '[Strategic View] Load Raster Comparison Success',
  props<{ comparisonKey: string; comparison: IStrategicComparisonResponse }>(),
);

export const loadStrategicRasterComparisonFailure = createAction(
  '[Strategic View] Load Raster Comparison Failure',
  props<{ comparisonKey: string; error: any }>(),
);


export const loadBiomapStrategicChange = createAction(
  '[Strategic View] Load BIOMAP Change',
  props<{
    projectUuid: string;
    rasterLayerUuid: string;
    product: BiomapStrategicChangeProduct;
    sourceBeforeUuid: string;
    sourceAfterUuid: string;
    forceRecompute?: boolean;
  }>(),
);

export const loadBiomapStrategicChangeSuccess = createAction(
  '[Strategic View] Load BIOMAP Change Success',
  props<{ changeKey: string; change: IBiomapStrategicChangeResponse }>(),
);

export const loadBiomapStrategicChangeFailure = createAction(
  '[Strategic View] Load BIOMAP Change Failure',
  props<{ changeKey: string; error: string }>(),
);



export const openBiomapInterventionAssessment = createAction(
  '[Strategic View] Open BIOMAP Intervention Assessment',
  props<{
    availability: IBiomapInterventionAvailability;
    selectedProducts: IBiomapInterventionProductSelection;
    habitatClassification: BiomapHabitatClassification;
  }>(),
);

export const closeBiomapInterventionAssessment = createAction(
  '[Strategic View] Close BIOMAP Intervention Assessment',
);

export const updateBiomapInterventionAvailability = createAction(
  '[Strategic View] Update BIOMAP Intervention Availability',
  props<{
    availability: IBiomapInterventionAvailability;
    selectedProducts: IBiomapInterventionProductSelection;
    habitatClassification: BiomapHabitatClassification;
  }>(),
);

export const setBiomapInterventionGeometry = createAction(
  '[Strategic View] Set BIOMAP Intervention Geometry',
  props<{ geometry: BiomapInterventionGeometry | null }>(),
);

export const setBiomapInterventionContextBuffer = createAction(
  '[Strategic View] Set BIOMAP Intervention Context Buffer',
  props<{ contextBufferM: number | null }>(),
);

export const setBiomapInterventionProductSelection = createAction(
  '[Strategic View] Set BIOMAP Intervention Product Selection',
  props<{ product: 'habitat' | 'msa'; selected: boolean }>(),
);

export const setBiomapInterventionHabitatClassification = createAction(
  '[Strategic View] Set BIOMAP Intervention Habitat Classification',
  props<{ habitatClassification: BiomapHabitatClassification }>(),
);

export const setBiomapInterventionDrawing = createAction(
  '[Strategic View] Set BIOMAP Intervention Drawing',
  props<{ drawing: boolean }>(),
);

export const setBiomapInterventionGeometryTarget = createAction(
  '[Strategic View] Set BIOMAP Intervention Geometry Target',
  props<{ target: BiomapInterventionGeometryTarget }>(),
);

export const setBiomapCompensationEnabled = createAction(
  '[Strategic View] Set BIOMAP Compensation Enabled',
  props<{ enabled: boolean }>(),
);

export const setBiomapCompensationGeometry = createAction(
  '[Strategic View] Set BIOMAP Compensation Geometry',
  props<{ geometry: BiomapInterventionGeometry | null }>(),
);

export const uploadBiomapInterventionShapefile = createAction(
  '[Strategic View] Upload BIOMAP Intervention Shapefile',
  props<{ projectUuid: string; file: File }>(),
);

export const uploadBiomapInterventionShapefileSuccess = createAction(
  '[Strategic View] Upload BIOMAP Intervention Shapefile Success',
  props<{ geometry: BiomapInterventionGeometry }>(),
);

export const uploadBiomapInterventionShapefileFailure = createAction(
  '[Strategic View] Upload BIOMAP Intervention Shapefile Failure',
  props<{ error: string }>(),
);

export const uploadBiomapCompensationShapefile = createAction(
  '[Strategic View] Upload BIOMAP Compensation Shapefile',
  props<{ projectUuid: string; file: File }>(),
);

export const uploadBiomapCompensationShapefileSuccess = createAction(
  '[Strategic View] Upload BIOMAP Compensation Shapefile Success',
  props<{ geometry: BiomapInterventionGeometry }>(),
);

export const uploadBiomapCompensationShapefileFailure = createAction(
  '[Strategic View] Upload BIOMAP Compensation Shapefile Failure',
  props<{ error: string }>(),
);

export const runBiomapInterventionAssessment = createAction(
  '[Strategic View] Run BIOMAP Intervention Assessment',
  props<{ plan: IBiomapInterventionRunPlan }>(),
);

export const loadBiomapHabitatIntervention = createAction(
  '[Strategic View] Load BIOMAP Habitat Intervention Assessment',
  props<{
    projectUuid: string;
    rasterLayerUuid: string;
    sourceBeforeUuid: string;
    sourceAfterUuid: string;
    interventionGeometry: BiomapInterventionGeometry;
    contextBufferM: number;
    forceRecompute?: boolean;
  }>(),
);

export const loadBiomapHabitatInterventionSuccess = createAction(
  '[Strategic View] Load BIOMAP Habitat Intervention Assessment Success',
  props<{ result: IBiomapHabitatInterventionResponse }>(),
);

export const loadBiomapHabitatInterventionFailure = createAction(
  '[Strategic View] Load BIOMAP Habitat Intervention Assessment Failure',
  props<{ error: string }>(),
);

export const loadBiomapMsaIntervention = createAction(
  '[Strategic View] Load BIOMAP MSA Intervention Assessment',
  props<{
    projectUuid: string;
    rasterLayerUuid: string;
    sourceBeforeUuid: string;
    sourceAfterUuid: string;
    interventionGeometry: BiomapInterventionGeometry;
    contextBufferM: number;
    forceRecompute?: boolean;
  }>(),
);

export const loadBiomapMsaInterventionSuccess = createAction(
  '[Strategic View] Load BIOMAP MSA Intervention Assessment Success',
  props<{ result: IBiomapMsaInterventionResponse }>(),
);

export const loadBiomapMsaInterventionFailure = createAction(
  '[Strategic View] Load BIOMAP MSA Intervention Assessment Failure',
  props<{ error: string }>(),
);

export const loadBiomapHabitatCompensation = createAction(
  '[Strategic View] Load BIOMAP Habitat Compensation',
  props<{
    projectUuid: string;
    rasterLayerUuid: string;
    sourceBeforeUuid: string;
    sourceAfterUuid: string;
    impactGeometry: BiomapInterventionGeometry;
    compensationGeometry: BiomapInterventionGeometry;
    forceRecompute?: boolean;
  }>(),
);

export const loadBiomapHabitatCompensationSuccess = createAction(
  '[Strategic View] Load BIOMAP Habitat Compensation Success',
  props<{ result: IBiomapHabitatCompensationResponse }>(),
);

export const loadBiomapHabitatCompensationFailure = createAction(
  '[Strategic View] Load BIOMAP Habitat Compensation Failure',
  props<{ error: string }>(),
);

export const loadBiomapMsaCompensation = createAction(
  '[Strategic View] Load BIOMAP MSA Compensation',
  props<{
    projectUuid: string;
    rasterLayerUuid: string;
    sourceBeforeUuid: string;
    sourceAfterUuid: string;
    impactGeometry: BiomapInterventionGeometry;
    compensationGeometry: BiomapInterventionGeometry;
    forceRecompute?: boolean;
  }>(),
);

export const loadBiomapMsaCompensationSuccess = createAction(
  '[Strategic View] Load BIOMAP MSA Compensation Success',
  props<{ result: IBiomapMsaCompensationResponse }>(),
);

export const loadBiomapMsaCompensationFailure = createAction(
  '[Strategic View] Load BIOMAP MSA Compensation Failure',
  props<{ error: string }>(),
);


export const openBiomapScenarioAnalysis = createAction(
  '[Strategic View] Open BIOMAP Scenario Analysis',
  props<{ projectUuid: string; rasterLayerUuid: string }>(),
);

export const loadBiomapScenarioCatalogSuccess = createAction(
  '[Strategic View] Load BIOMAP Scenario Catalog Success',
  props<{ catalog: IBiomapScenarioCatalog; selection: IBiomapScenarioSelection }>(),
);

export const loadBiomapScenarioCatalogFailure = createAction(
  '[Strategic View] Load BIOMAP Scenario Catalog Failure',
  props<{ error: string }>(),
);

export const closeBiomapScenarioAnalysis = createAction(
  '[Strategic View] Close BIOMAP Scenario Analysis',
);

export const configureBiomapScenarioAnalysis = createAction(
  '[Strategic View] Configure BIOMAP Scenario Analysis',
  props<{ selection: IBiomapScenarioSelection }>(),
);

export const runBiomapScenarioAnalysis = createAction(
  '[Strategic View] Run BIOMAP Scenario Analysis',
  props<{
    projectUuid: string;
    rasterLayerUuid: string;
    request: IBiomapMsaScenarioAnalysisRequest;
  }>(),
);

export const runBiomapScenarioAnalysisSuccess = createAction(
  '[Strategic View] Run BIOMAP Scenario Analysis Success',
  props<{ result: IBiomapMsaScenarioAnalysisResponse }>(),
);

export const runBiomapScenarioAnalysisFailure = createAction(
  '[Strategic View] Run BIOMAP Scenario Analysis Failure',
  props<{ error: string }>(),
);

export const clearStrategicViewState = createAction('[Strategic View] Clear State');
